import { Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable, type DataTableState, type IDataColumn } from '@/design';
import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IListField, IListFieldCatalogue, IListFieldThreshold } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { AdminPage, LoadError, useAdminData } from '../../components/admin-page';
import { NOT_ARRIVED } from '../../components/field-controls';
import { LIST_SURVEYS, listSurveyLabel, sourceLabel } from '../../lists';
import { refusalMessage } from '../../refusal';
import { useImportTabs } from './area';

/**
 * Import → Thresholds (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §5.5, the owner's Q6) — how far a value a file writes may move before the check holds it as
 * unusual, per survey and field: `PUT /admin/list-fields/{code}/anomaly-threshold?survey=`, behind
 * `internal_fields.author` (the route's policy, and this screen's gate).
 *
 * **Which fields take one is survey-api's rule, read off its catalogue** (`ListFieldThresholdRules`):
 * an editable staff field of kind number, money or percent, and a figure series by its undated code
 * (`fig.<metric>.<scope>`). The catalogue carries each one's `anomalyThreshold` as a fraction; the box
 * says it as a percent — "Hold a change of more than [25] %", empty for off.
 *
 * **A change is sent on blur or Enter**, and judged there: plain decimal text is sent as its
 * fraction, an emptied box as `null`, anything else held with what is wanted and sent nothing; a 400
 * is said beside the box in survey-api's words. The answer's `setBy` and `setAt` are drawn — **the
 * catalogue carries neither**, so who set a threshold before this visit is not said (a gap in the
 * contract, the design's §11).
 */

const MEASURED_KINDS = new Set(['number', 'money', 'percent']);

/** Whether a field takes a threshold — `ListFieldThresholdRules.CodeProblem`'s mirror, over the
 *  catalogue as the reader may see it. */
function takesThreshold(field: IListField, source: string): boolean {
  if (source === 'figure') return /^fig\.[^.]+\.[^.]+$/.test(field.code);
  return source === 'internal' && field.editable && MEASURED_KINDS.has(field.kind);
}

/** A fraction as the box's percent: `0.25` → "25", never `7.000000000000001`. */
function percentText(threshold: Nullable<number>): string {
  return threshold === null ? '' : String(Number((threshold * 100).toPrecision(12)));
}

const PLAIN_DECIMAL = /^\d+(\.\d+)?$/;

interface IRow {
  field: IListField;
  source: string;
}

export function AdminImportThresholdsPage() {
  const [params, setParams] = useSearchParams();
  const survey = params.get('survey') || 'FA';
  const tabs = useImportTabs();
  const { data, error, reload } = useAdminData(
    async (signal) => ({ survey, catalogue: await listsApi.fields(survey, signal) }),
    [survey]
  );
  const answer = data?.survey === survey ? data : null;
  const rows = React.useMemo(() => (answer?.catalogue ? measured(answer.catalogue) : null), [answer]);
  // What each write answered this visit, by survey and field — the catalogue says no author.
  const [answered, setAnswered] = React.useState<ReadonlyMap<string, IListFieldThreshold>>(new Map());
  const answeredOf = (code: string) => answered.get(`${survey}:${code}`);
  const none = <span className="text-muted-foreground">—</span>;

  const columns: IDataColumn<IRow>[] = [
    {
      id: 'field',
      header: 'Field',
      className: 'font-medium',
      cell: (row) => (
        <span className="grid">
          {row.field.label}
          <span className="text-xs font-normal text-muted-foreground">{sourceLabel(row.source)}</span>
        </span>
      )
    },
    {
      id: 'threshold',
      header: 'Hold a change of more than',
      cell: (row) => (
        <ThresholdBox
          key={`${survey}:${row.field.code}`}
          survey={survey}
          field={row.field}
          onGone={() => void reload()}
          onAnswered={(saved) =>
            setAnswered((now) => new Map(now).set(`${survey}:${row.field.code}`, saved))
          }
        />
      )
    },
    {
      id: 'by',
      header: 'Set by',
      cell: (row) => answeredOf(row.field.code)?.setBy.email ?? none
    },
    {
      id: 'when',
      header: 'When',
      className: 'whitespace-nowrap',
      cell: (row) => {
        const saved = answeredOf(row.field.code);
        return saved ? formatDateTime(saved.setAt) : none;
      }
    }
  ];

  let state: DataTableState;
  if (answer === null) {
    state = error ? { kind: 'error', content: <LoadError message={error} /> } : { kind: 'loading' };
  } else {
    state = {
      kind: 'empty',
      content: `No field of ${listSurveyLabel(survey)} takes a threshold yet — a number, money or percent staff field, or a figure.`
    };
  }

  return (
    <AdminPage title="Import" documentTitle="Import thresholds" crumb="Cycle" tabs={tabs}>
      <p className="max-w-3xl text-sm text-muted-foreground">
        A file's change to one of these fields is held at the check when it moves the value by more
        than the percent set here; a held change is written only when it is included. An empty box
        holds nothing.
      </p>
      <label className="flex items-center gap-2 text-sm">
        Survey
        <select
          value={survey}
          onChange={(event) => setParams(new URLSearchParams({ survey: event.target.value }))}
          className="h-9 rounded-md border border-border bg-white px-2"
        >
          {(LIST_SURVEYS.some((one) => one.code === survey)
            ? LIST_SURVEYS
            : [...LIST_SURVEYS, { code: survey, label: survey }]
          ).map((one) => (
            <option key={one.code} value={one.code}>
              {one.label}
            </option>
          ))}
        </select>
      </label>
      {error && answer !== null && (
        <LoadError message={`Could not refresh — showing the fields as last read. ${error}`} />
      )}
      {answer?.catalogue === null ? (
        <p role="status" className="py-8 text-center text-muted-foreground">
          This survey is not on this deployment.
        </p>
      ) : (
        <DataTable
          label={`${listSurveyLabel(survey)} thresholds`}
          columns={columns}
          rows={rows === null || rows.length === 0 ? null : rows}
          rowKey={(row) => row.field.code}
          rowProps={(row) => ({ 'data-threshold': row.field.code }) as React.HTMLAttributes<HTMLTableRowElement>}
          state={state}
        />
      )}
      {rows !== null && rows.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Set by and When are drawn for a threshold set on this visit — the field list does not say
          who set one before.
        </p>
      )}
    </AdminPage>
  );
}

function measured(catalogue: IListFieldCatalogue): IRow[] {
  return catalogue.groups.flatMap((group) =>
    group.fields
      .filter((field) => takesThreshold(field, group.source))
      .map((field) => ({ field, source: group.source }))
  );
}

/** One field's box: its stored threshold, the draft typed, and what the last write answered. */
function ThresholdBox({
  survey,
  field,
  onGone,
  onAnswered
}: {
  survey: string;
  field: IListField;
  onAnswered: (saved: IListFieldThreshold) => void;
  /** survey-api no longer knows the survey or the field: the catalogue is read again. */
  onGone: () => void;
}) {
  const [stored, setStored] = React.useState<Nullable<number>>(field.anomalyThreshold);
  const [draft, setDraft] = React.useState(percentText(field.anomalyThreshold));
  const [saving, setSaving] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const id = `threshold-${field.code}`;

  const send = async () => {
    const text = draft.trim();
    if (text === percentText(stored)) return;
    if (text !== '' && !PLAIN_DECIMAL.test(text)) {
      setSaid('Type the percent as a number, such as 25 — nothing was saved.');
      return;
    }
    const threshold = text === '' ? null : Number(text) / 100;
    setSaving(true);
    setSaid(null);
    try {
      const saved = await listsApi.setThreshold(survey, field.code, threshold);
      setStored(saved.threshold);
      setDraft(percentText(saved.threshold));
      onAnswered(saved);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        setSaid(refusalMessage(error, 'The threshold was refused.'));
      } else if (error instanceof ApiError && error.status === 403) {
        setSaid('Your Team Portal roles do not include setting thresholds.');
      } else if (error instanceof ApiError && error.status === 404) {
        setSaid('This survey or field is no longer there — the fields are read again.');
        onGone();
      } else if (!(error instanceof ApiError) || error.status >= 500) {
        setSaid(`${NOT_ARRIVED} Read the page again to see whether the threshold was saved.`);
      } else {
        setSaid(refusalMessage(error, 'The threshold was not saved.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-1">
      <span className="flex items-center gap-1.5">
        <Input
          id={id}
          value={draft}
          inputMode="decimal"
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => void send()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void send();
            }
          }}
          aria-label={`Hold a change of more than — ${field.label}, in percent`}
          aria-describedby={said ? `${id}-said` : undefined}
          className="h-8 w-24"
        />
        <span aria-hidden>%</span>
        {saving && <span className="text-sm text-muted-foreground">Saving…</span>}
      </span>
      {said && (
        <p id={`${id}-said`} role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
    </div>
  );
}
